import { Component, OnInit, OnDestroy } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import { FormService } from '../form.service';
import { Subscription } from 'rxjs';
import { AgGridOptionsBuilder, AgGridColumns, AgGridFilterParams } from '../../AgGrid';
import { GridApi, GridReadyEvent, ColDef, GridOptions } from 'ag-grid-community';
import { AgGridMultiSelectTracker, AgGridMultiSelectedHeaderRenderer, AgGridMultiSelectedCellRenderer, AgGridMultiSelectRendererParams } from '../../AgGrid/MultiSelectTracker';
import { BusyIndicatorMessageManager } from '../../../Busy-Indicator';
import { IWeissmanModalComponent } from '../../WeissmanModalService';

@Component({
    selector: 'form-factor-table-add',
    templateUrl: './formFactorTableAdd.component.html',
    styleUrls: ['formFactorTableAdd.component.scss'],
    standalone: false
})
export class FormFactorTableAddComponent implements OnInit, OnDestroy, IWeissmanModalComponent<void, Compliance.FactorTableListItemModel[]> {
    constructor(
        private readonly _formService: FormService,
        private readonly _bsModalRef: BsModalRef) {
    }

    gridApi: GridApi;

    params: void;
    result: Compliance.FactorTableListItemModel[];

    gridTracker: AgGridMultiSelectTracker;

    gridOptions: GridOptions = new AgGridOptionsBuilder({
        rowClassRules: {
            'ag-row-selected': (params) => params.data && this.gridTracker.isRowSelected((params.data as Compliance.FactorTableListItemModelSelectable).factorTableId)
        }
    })
        .withContext(this)
        .withLoadingOverlay()
        .withColumnResize()
        .withFloatingFilter()
        .withMultipleColumnSort()
        .withTextSelection()
        .build();

    busyIndicatorMessageManager: BusyIndicatorMessageManager<string> = new BusyIndicatorMessageManager<string>();
    rowsSelected: boolean = false;

    private _selectedRowsSub: Subscription;
    private _availableFactorTables: Compliance.FactorTableListItemModelSelectable[] = null;

    ngOnInit(): void {
        const result = _.filter(this._formService.allFactorTables, (aft) => !_.some(this._formService.factorTables, (fft) => {
            return aft.factorTableId === fft.factorTableId;
        }));
        this._availableFactorTables = _.map(result, (fft) => {
            return { ...fft, isSelected: false } as Compliance.FactorTableListItemModelSelectable;
        });
        this._setRowData();
    }

    ngOnDestroy(): void {
        if (this._selectedRowsSub) {
            this._selectedRowsSub.unsubscribe();
        }
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
        this.gridTracker = new AgGridMultiSelectTracker(this.gridOptions, this.gridApi, this._getGridRowIds.bind(this));
        this._selectedRowsSub = this.gridTracker.selectedRows$.subscribe(x => {
            this.rowsSelected = x.selectAllRows || x.selectedRows.length > 0;
        });

        const columns: ColDef[] = [
            {
                field: 'factorTableId',
                width: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressMovable: true,
                suppressColumnsToolPanel: true,
                pinned: 'left',
                lockPinned: true,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: {
                    tracker: this.gridTracker
                } as AgGridMultiSelectRendererParams,
                cellRenderer: AgGridMultiSelectedCellRenderer,
                cellRendererParams: {
                    tracker: this.gridTracker
                } as AgGridMultiSelectRendererParams
            },
            {
                headerName: 'Assessor',
                field: 'assessorName',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueFormatter: (params) => {
                    const factorTable = params.data as Compliance.FactorTableListItemModel;
                    if (!factorTable) {
                        return '';
                    }
                    return factorTable.assessorName ? factorTable.assessorName : factorTable.state;
                },
                filterValueGetter: params => {
                    const factorTable = params.data as Compliance.FactorTableListItemModel;
                    if (!factorTable) {
                        return '';
                    }
                    return factorTable.assessorName ? factorTable.assessorName : factorTable.state;
                },
                cellClass: params => {
                    const factorTable = params.data as Compliance.FactorTableListItemModel;
                    if (!factorTable) {
                        return '';
                    }
                    return factorTable.assessorName ? '' : 'assessor-state';
                }
            },
            {
                headerName: 'Name',
                field: 'name',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth
            },
            {
                headerName: 'Life',
                type: 'numericColumn',
                field: 'life',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Type',
                field: 'tableType',
                filterValueGetter: x => {
                    return (x.data) ? this._formService.getFactorTableTypeName(x.data.tableType) : '';
                },
                valueFormatter: x => {
                    return (x.data) ? this._formService.getFactorTableTypeName(x.data.tableType) : '';
                },
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            }
        ];

        this.gridApi.setGridOption('columnDefs', columns);
        this._setRowData();
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    async save(): Promise<void> {
        const selectedRows = await this.gridTracker.getSelectedRowIds();
        const nodes = [];
        this.gridApi.forEachNode(x => {
            const ft = x.data as Compliance.FactorTableListItemModel;
            if (selectedRows.includes(ft.factorTableId)) {
                nodes.push(ft);
            }
        });
        this.result = nodes;
        this._bsModalRef.hide();
    }

    private _setRowData(): void {
        if (!(this.gridApi && this._formService.isInitialized)) {
            return;
        }

        this.gridApi.setGridOption('rowData', this._availableFactorTables);
        this.gridApi.sizeColumnsToFit();
    }

    private _getGridRowIds(skip, take): Promise<Compliance.QueryResultModel<number>> {
        const model = { rowsToDisplay: [] };
        this.gridApi.forEachNodeAfterFilterAndSort(x => model.rowsToDisplay.push(x));
        const rows = model.rowsToDisplay.slice(skip, take + 1);
        return Promise.resolve({
            data: rows.map(x => {
                const factorTable = x.data as Compliance.FactorTableListItemModel;
                return factorTable && factorTable.factorTableId;
            })
        } as Compliance.QueryResultModel<number>);
    }
}
